import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  IconButton,
  useTheme,
  useMediaQuery,
  Chip,
  Stack,
  Link,
  Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import NavigationSidebar from "../NavigationSidebar";
import InfoIcon from "@mui/icons-material/Info";
import ReactMarkdown from "react-markdown";
import ChatTemplates from "./ChatTemplates";
import MenuIcon from "@mui/icons-material/Menu";
import Marquee from "react-fast-marquee";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SimilarQuestions from "./SimilarQuestions";
import KeywordHighlighter from "./KeywordHighlighter";
import TextGenerateEffect from "./TextGenerateEffect";

const gradients = [
  "linear-gradient(45deg, rgb(58, 59, 60), rgb(74, 77, 79))", // Darker blue gradient
  "linear-gradient(45deg, rgb(58, 59, 60), rgb(74, 77, 79))", // Darker green gradient
  "linear-gradient(45deg, rgb(58, 59, 60), rgb(74, 77, 79))", // Darker purple gradient
  "linear-gradient(45deg, rgb(58, 59, 60), rgb(74, 77, 79))", // Darker orange gradient
];

const ThinkingAnimation = () => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 2 }}>
      <Typography>Thinking</Typography>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          gap: 0.5,
          alignItems: "center",
          "& .dot": {
            width: 4,
            height: 4,
            backgroundColor: "text.primary",
            borderRadius: "50%",
            animation: "dotPulse 1.5s infinite",
            "&:nth-of-type(2)": {
              animationDelay: "0.2s",
            },
            "&:nth-of-type(3)": {
              animationDelay: "0.4s",
            },
          },
          "@keyframes dotPulse": {
            "0%, 100%": {
              transform: "scale(0.5)",
              opacity: 0.5,
            },
            "50%": {
              transform: "scale(1)",
              opacity: 1,
            },
          },
        }}
      >
        <Box component="span" className="dot" />
        <Box component="span" className="dot" />
        <Box component="span" className="dot" />
      </Box>
    </Box>
  );
};

const TypingMessage = ({ text, onComplete }) => {
  const [displayText, setDisplayText] = useState("");

  useEffect(() => {
    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= text.length) {
        setDisplayText(text.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
        if (onComplete) onComplete();
      }
    }, 20); // Adjust speed here (lower = faster)

    return () => clearInterval(interval);
  }, [text, onComplete]);

  return <Typography>{displayText}</Typography>;
};

const AIChatPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [input, setInput] = useState("");
  const [currentChat, setCurrentChat] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(!isMobile);
  const messagesEndRef = useRef(null);
  const resizeTimeoutRef = useRef(null);
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [sourceDocuments, setSourceDocuments] = useState([]);
  const [username, setUsername] = useState("");
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);
  const [hasShownInitialGreeting, setHasShownInitialGreeting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [hasShownMarquee, setHasShownMarquee] = useState(false);
  const [animatingMessageIndex, setAnimatingMessageIndex] = useState(null);
  const [completedAnimations, setCompletedAnimations] = useState(new Set());
  const [messageDisplayComplete, setMessageDisplayComplete] = useState({});

  // Add effect to hide navigation when component mounts
  useEffect(() => {
    // Add hideNav query parameter to URL without navigating
    const url = new URL(window.location.href);
    url.searchParams.set("hideNav", "true");
    window.history.replaceState({}, "", url.toString());

    // Cleanup when component unmounts
    return () => {
      const url = new URL(window.location.href);
      url.searchParams.delete("hideNav");
      window.history.replaceState({}, "", url.toString());
    };
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/user/${userId}/profile-data`
        );
        const data = await response.json();

        if (data.success && data.profile && currentChat.length === 0) {
          setUsername(data.profile.name);
          setCurrentChat([
            {
              role: "assistant",
              content: `Hi ${data.profile.name}, How can I help with your career today?`,
              isInitialGreeting: true,
            },
          ]);
          setHasShownInitialGreeting(true);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    fetchUserProfile();
  }, [userId, currentChat.length]);

  useEffect(() => {
    setShowHistory(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  const scrollToBottom = () => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    resizeTimeoutRef.current = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  useEffect(() => {
    scrollToBottom();
  }, [currentChat]);

  const sendMessage = async (message) => {
    try {
      setIsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId,
          question: message,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        setCurrentChat((prev) => [
          ...prev,
          { role: "user", content: message },
          {
            role: "assistant",
            content: data.message || "An error occurred. Please try again.",
            isError: true,
          },
        ]);
        return;
      }

      const newMessageIndex = currentChat.length + 1;
      setCurrentChat((prev) => [
        ...prev,
        { role: "user", content: message },
        {
          role: "assistant",
          content: data.message,
          sources: data.sourceDocuments,
          keywords: data.keywords,
        },
      ]);
      setAnimatingMessageIndex(newMessageIndex);

      setFollowUpQuestions(data.followUpQuestions || []);
      setInput("");
    } catch (error) {
      console.error("Error sending message:", error);
      setCurrentChat((prev) => [
        ...prev,
        { role: "user", content: message },
        {
          role: "assistant",
          content: "I apologize, but I encountered an error. Please try again.",
          isError: true,
        },
      ]);
    } finally {
      setIsLoading(false);
      setHasShownMarquee(true);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (input.trim()) {
        sendMessage(input);
      }
    }
  };

  const FollowUpQuestions = ({ questions }) => {
    if (!questions || questions.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", ml: 1, mb: 1, display: "block" }}
        >
          Suggested questions:
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {questions.map((question, index) => (
            <Chip
              key={index}
              label={question}
              onClick={() => {
                sendMessage(question);
                setFollowUpQuestions([]); // Clear follow-ups when one is selected
              }}
              sx={{
                bgcolor: "primary.light",
                color: "white",
                "&:hover": {
                  bgcolor: "primary.main",
                },
                maxWidth: { xs: "100%", sm: "300px" },
                height: "auto",
                "& .MuiChip-label": {
                  whiteSpace: "normal",
                  padding: "8px",
                },
                my: 0.5,
              }}
            />
          ))}
        </Stack>
      </Box>
    );
  };

  const SourceDocuments = ({ sources }) => {
    if (!sources || sources.length === 0) return null;

    return (
      <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <Tooltip title="Sources used for this response">
          <InfoIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        </Tooltip>
        <Typography variant="caption" color="text.secondary">
          Sources:{" "}
          {sources.map((source, index) => (
            <React.Fragment key={source.id}>
              {index > 0 && ", "}
              <Tooltip
                title={`Relevance: ${Math.round(source.relevance * 100)}%`}
              >
                <Link
                  component="span"
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    /* Handle document click */
                  }}
                >
                  {source.title}
                </Link>
              </Tooltip>
            </React.Fragment>
          ))}
        </Typography>
      </Box>
    );
  };

  const handleTemplateSelect = (templateText) => {
    setInput(templateText);
    sendMessage(templateText);
    setHasShownMarquee(true);
  };

  // Remove or modify this effect to prevent templates sidebar from showing on load
  useEffect(() => {
    // Only update template visibility when user manually toggles
    if (!location.pathname.includes("/ai-chat")) {
      setIsTemplatesOpen(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/ai/templates`
        );
        const data = await response.json();
        // If no templates from API, use default templates from ChatTemplates
        if (!data || data.length === 0) {
          setTemplates([
            {
              title: "Tell me more about my Career Report",
              prompt: "Tell me more about my Career Report",
            },
            {
              title: "What Career Animal am I?",
              prompt: "Tell more about my career animal",
            },
            // ... other default templates
          ]);
        } else {
          setTemplates(data);
        }
      } catch (error) {
        console.error("Error fetching templates:", error);
        // Set default templates on error
        setTemplates([
          {
            title: "Tell me more about my Career Report",
            prompt: "Tell me more about my Career Report",
          },
          {
            title: "What Career Animal am I?",
            prompt: "Tell more about my career animal",
          },
          {
            title: "What's my career direction?",
            prompt: "What's my career direction?",
          },
          {
            title: "How can I improve my career health?",
            prompt: "Give me some ideas on how can I improve my career health?",
          },
          {
            title:
              "What could make me happier at work? (what are my dissatisfiers)",
            prompt:
              "What could make me happier at work? (what are my dissatisfiers)",
          },
          {
            title: "How do I progress my career?",
            prompt: "Give me suggestion on how do I progress my career?",
          },
          {
            title: "What skills should I enhance?",
            prompt: "What skills should I enhance?",
          },
        ]);
      }
    };

    fetchTemplates();
  }, []);

  const handleSelectSimilarQuestion = (question) => {
    setInput(question);
  };

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <NavigationSidebar userId={userId} />

      {/* Main Chat Area */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          bgcolor: "#f5f5f5",
          position: "relative",
          overflow: "hidden",
          transition: "margin 0.3s ease",
          marginRight: isTemplatesOpen ? "300px" : 0,
        }}
      >
        {/* Messages Container */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Messages */}
          <Box
            sx={{
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {currentChat.map((message, index) => (
              <Box
                key={index}
                sx={{
                  alignSelf:
                    message.role === "user" ? "flex-end" : "flex-start",
                  maxWidth: { xs: "85%", sm: "70%" },
                }}
              >
                {message.role === "user" ? (
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      bgcolor: "primary.main",
                      color: "white",
                      borderRadius: "20px 20px 0 20px",
                      wordBreak: "break-word",
                    }}
                  >
                    <Typography>{message.content}</Typography>
                  </Paper>
                ) : (
                  <Box>
                    {message.isInitialGreeting && !hasShownInitialGreeting ? (
                      <Box sx={{ p: 2 }}>
                        <TextGenerateEffect
                          text={message.content}
                          onComplete={() => {
                            setIsTypingComplete(true);
                            setHasShownInitialGreeting(true);
                          }}
                        />
                      </Box>
                    ) : (
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          bgcolor: "background.paper",
                          borderRadius: "20px 20px 20px 0",
                          wordBreak: "break-word",
                        }}
                      >
                        {!completedAnimations.has(index) &&
                        index === animatingMessageIndex ? (
                          <TextGenerateEffect
                            text={message.content}
                            onComplete={() => {
                              setCompletedAnimations(
                                (prev) => new Set([...prev, index])
                              );
                              setAnimatingMessageIndex(null);
                              setMessageDisplayComplete((prev) => ({
                                ...prev,
                                [index]: true,
                              }));
                            }}
                          />
                        ) : (
                          <Typography variant="body1">
                            {message.isError ? (
                              <span style={{ color: "red" }}>
                                {message.content}
                              </span>
                            ) : (
                              <KeywordHighlighter
                                content={message.content}
                                keywords={message.keywords || []}
                                onKeywordClick={sendMessage}
                              />
                            )}
                          </Typography>
                        )}
                      </Paper>
                    )}
                    <SourceDocuments sources={message.sources} />
                    {index === currentChat.length - 1 &&
                      !isLoading &&
                      (completedAnimations.has(index) ||
                        messageDisplayComplete[index]) && (
                        <FollowUpQuestions questions={followUpQuestions} />
                      )}
                  </Box>
                )}
              </Box>
            ))}
            {isLoading && <ThinkingAnimation />}
            <div ref={messagesEndRef} />
          </Box>
        </Box>

        {/* Marquee Section - Always show when templates aren't in sidebar */}
        {!hasShownMarquee && templates.length > 0 && (
          <Box
            sx={{
              bgcolor: "background.paper",
              borderTop: 1,
              borderColor: "divider",
              py: 2,
            }}
          >
            <Marquee
              gradient={false}
              speed={15}
              direction="left"
              pauseOnHover={true}
            >
              <Box sx={{ display: "flex", gap: 2, px: 2 }}>
                {templates.slice(0, 4).map((template, index) => (
                  <Chip
                    key={index}
                    label={template.title}
                    onClick={() => {
                      handleTemplateSelect(template.prompt);
                    }}
                    sx={{
                      cursor: "pointer",
                      mx: 1,
                      background: gradients[index % gradients.length],
                      color: "white",
                      fontWeight: 500,
                      "&:hover": {
                        opacity: 0.9,
                        transform: "scale(1.05)",
                      },
                      maxWidth: { xs: "100%", sm: "300px" },
                      height: "auto",
                      "& .MuiChip-label": {
                        whiteSpace: "normal",
                        padding: "8px",
                        textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  />
                ))}
              </Box>
            </Marquee>
            <Marquee
              gradient={false}
              speed={15}
              direction="right"
              pauseOnHover={true}
            >
              <Box sx={{ display: "flex", gap: 2, px: 2, mt: 1 }}>
                {templates.slice().map((template, index) => (
                  <Chip
                    key={index}
                    label={template.title}
                    onClick={() => {
                      handleTemplateSelect(template.prompt);
                    }}
                    sx={{
                      cursor: "pointer",
                      mx: 1,
                      background: gradients[index % gradients.length],
                      color: "white",
                      fontWeight: 500,
                      "&:hover": {
                        opacity: 0.9,
                        transform: "scale(1.05)",
                      },
                      maxWidth: { xs: "100%", sm: "300px" },
                      height: "auto",
                      "& .MuiChip-label": {
                        whiteSpace: "normal",
                        padding: "8px",
                        textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  />
                ))}
              </Box>
            </Marquee>
          </Box>
        )}

        {/* Input Area */}
        <Box
          sx={{
            p: 2,
            bgcolor: "background.paper",
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <SimilarQuestions
            userId={userId}
            currentQuestion={input}
            onSelectQuestion={handleSelectSimilarQuestion}
          />

          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message here..."
            disabled={isLoading}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => input.trim() && sendMessage(input)}
                  disabled={isLoading || !input.trim()}
                >
                  <SendIcon />
                </IconButton>
              ),
            }}
          />
        </Box>
      </Box>

      {/* Ideas Sidebar - Fixed width */}
      {isTemplatesOpen && (
        <Paper
          sx={{
            width: 300,
            height: "100%",
            position: "fixed",
            right: 0,
            top: 0,
            zIndex: theme.zIndex.drawer - 1,
            flexShrink: 0,
            borderLeft: 1,
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ChatTemplates
            onTemplateSelect={handleTemplateSelect}
            isOpen={isTemplatesOpen}
            onToggle={() => setIsTemplatesOpen(!isTemplatesOpen)}
          />
        </Paper>
      )}

      {/* Ideas Toggle Button - Adjust position */}
      {!isTemplatesOpen && (
        <IconButton
          sx={{
            position: "fixed",
            top: 8,
            right: { xs: 8, sm: isTemplatesOpen ? 308 : 8 },
            display: "flex",
            zIndex: 1,
            transition: "right 0.3s ease",
          }}
          onClick={() => setIsTemplatesOpen(true)}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ mr: 1 }}>Ideas</Typography>
            <ChevronRightIcon />
          </Box>
        </IconButton>
      )}
    </Box>
  );
};

export default AIChatPage;
